import { useEffect, useRef } from 'react'
import { ColumnFilterElementTemplateOptions } from 'primereact/column'
import { InputText } from 'primereact/inputtext'

/** Rango numérico de un filtro de columna: `buildFieldFilters` del framework lo manda como «mayor o igual» + «menor o igual». */
export interface NumberRange {
  min?: string | number | null
  max?: string | number | null
}

export interface NumberRangeLabels {
  /** Placeholder y aria-label del campo desde. */
  from: string
  /** Placeholder y aria-label del campo hasta. */
  to: string
}

const APPLY_DELAY_MS = 500

function NumberRangeFilter({ options, labels, integer }: {
  options: ColumnFilterElementTemplateOptions
  labels: NumberRangeLabels
  integer: boolean
}) {
  // Solo enteros: el backend rechaza un valor con decimales, así que el campo ni deja escribir el punto ni el signo
  const keyfilter = integer ? 'pint' : 'num'
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  // el mismo valor que lee DateFilterTemplate del framework: el del modelo, para no perder el otro extremo
  const current = ((options as { filterModel?: { value?: NumberRange } }).filterModel?.value
    ?? (options.value as NumberRange | undefined)) ?? {}

  function apply(patch: NumberRange) {
    // nunca `undefined`: el framework lo interpreta como «es nulo». Con ambos vacíos no genera ningún filtro.
    const next: NumberRange = { min: current.min ?? '', max: current.max ?? '', ...patch }
    options.filterCallback(next)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => options.filterApplyCallback(next), APPLY_DELAY_MS)
  }

  return (
    <div className="flex flex-row nv-date-filter">
      <InputText
        keyfilter={keyfilter}
        value={String(current.min ?? '')}
        placeholder={labels.from}
        aria-label={labels.from}
        onChange={(e) => apply({ min: e.target.value })}
      />
      <InputText
        keyfilter={keyfilter}
        value={String(current.max ?? '')}
        placeholder={labels.to}
        aria-label={labels.to}
        onChange={(e) => apply({ max: e.target.value })}
      />
    </div>
  )
}

/**
 * Control de filtro de columna con dos campos, desde y hasta, para enteros y decimales (`filtersType` con `ETypes.integer` o
 * `ETypes.bigDecimal`). Con `integer: true` solo deja escribir dígitos (para un campo que el backend rechaza con decimales).
 */
export const numberRangeFilterTemplate = (labels: NumberRangeLabels, { integer = false }: { integer?: boolean } = {}) =>
  (options: ColumnFilterElementTemplateOptions) => <NumberRangeFilter options={options} labels={labels} integer={integer} />

/** Texto del chip y del resumen de filtros de la barra «+ Filtro»: «18 – 65», «desde 18» o «hasta 65». */
export function formatNumberRange(value: unknown, labels: NumberRangeLabels): string {
  const range = (value ?? {}) as NumberRange
  const min = range.min === null || range.min === undefined || range.min === '' ? undefined : String(range.min)
  const max = range.max === null || range.max === undefined || range.max === '' ? undefined : String(range.max)
  if (min && max) return `${min} – ${max}`
  if (min) return `${labels.from} ${min}`
  return max ? `${labels.to} ${max}` : ''
}
